/**
 * PermissionGate — conditionally renders children based on permissions.
 *
 * Usage:
 *   <PermissionGate permission="agents:create">
 *     <button>Create Agent</button>
 *   </PermissionGate>
 *
 *   <PermissionGate anyOf={["agents:update", "agents:delete"]}>
 *     <button>Manage</button>
 *   </PermissionGate>
 *
 *   <PermissionGate permission="billing:manage" fallback={<span>No access</span>}>
 *     <BillingPanel />
 *   </PermissionGate>
 *
 *   <PermissionGate permission="agents:delete" pending={<Skeleton />}>
 *     <button>Delete</button>
 *   </PermissionGate>
 */

import { usePermissions } from './PermissionContext';

export default function PermissionGate({
    permission,
    anyOf,
    allOf,
    fallback = null,
    pending,
    children,
}) {
    const {
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        permissionsLoaded,
    } = usePermissions();

    // Same branch order as PermissionRoute: wait, then evaluate.
    //
    // NOTE: no call site passes `pending` yet. Omitted, this returns `fallback`
    // — byte-for-byte the old behaviour — so the hidden-then-visible flash on
    // first load still happens everywhere. This is the hook for fixing that
    // (pass a skeleton where the flash is visible), not the fix itself. What
    // actually closes N7 is PermissionFailureBanner: it distinguishes "your
    // permissions could not be verified" from "you are not allowed".
    if (!permissionsLoaded) {
        return pending === undefined ? fallback : pending;
    }

    // Permissions are the only input. When the fetch fails the set is empty, so
    // every gate closes — never fall back to a client-held role (VAPT-2026-001).
    // A failed fetch is NOT distinguishable here on purpose: it must fail closed.
    // PermissionFailureBanner is what makes that state visible to the user,
    // because permissionsLoaded is set true even on failure.
    let allowed = false;

    if (permission) {
        allowed = hasPermission(permission);
    } else if (anyOf) {
        allowed = hasAnyPermission(...anyOf);
    } else if (allOf) {
        allowed = hasAllPermissions(...allOf);
    }

    return allowed ? children : fallback;
}
